// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// One owner for "give me this machine" (G1-01 … G1-10; FR-021, FR-022, FR-023).
//
// Two routes start a live game — `/` and `/play/:machineId` — and until this module both of them
// were wired to the one definition in the bundle. `PlayRoute` held a one-element `Set` of known
// ids and answered 404 to everything else; `Arcade` built its tiles from the same bytes at module
// load. The Builder could publish a machine, the server would serve it, the leaderboard would rank
// it, and no web route could open it.
//
// **The mechanism was already here and nothing below invents one.** `GhostRoute` and
// `account/onlineGame` have fetched a definition by version since M8, and `MachineRuntime` takes
// `machine: unknown` and runs whatever it is handed. What was missing was a single answer to the
// question "which machine, and where from" — so this is that answer, and the routes call it.
//
// THE ORDER IS BUNDLE, THEN CACHE, THEN NETWORK, AND THE FIRST STEP IS LOAD-BEARING
// ---------------------------------------------------------------------------------
// **The flagship is never fetched.** FR-030 and NFR-006 both rest on the front door reaching
// nothing at all, `scripts/check-anonymous.mjs` is what says so mechanically, and a resolver that
// asked the network for NEON NOVA "for consistency" would break an acceptance criterion to save a
// branch. A stranger on `/` or on `/play/neon-nova` makes zero requests, on any connection, signed
// in or not.
//
// The cache is content-addressed, which is correct here rather than merely convenient: a published
// version is immutable (04 §1), so a hash is the same bytes for ever and there is no invalidation
// rule to get wrong. See `src/storage/machineCache.ts`.
//
// THREE REFUSALS, AND A SILENT FALLBACK IS NOT ONE OF THEM
// --------------------------------------------------------
// `PlayRoute`'s own header has said since M3-37 why a machine that cannot be served must never
// quietly become the flagship: a shared link would produce a different game for the person who
// receives it, and a link to somebody's deleted machine would become a link to NEON NOVA. So there
// are exactly three ways to fail and each says which one it is —
//
//   * `unknown` — no machine of that id is on the roster. A 404 page.
//   * `needs-pro` — it exists and this viewer may not have it. The paywall, at the moment of
//     desire, which is the only moment an upsell is honest.
//   * `unreadable` — it exists, it was fetched, and it is not a machine this build can run. A named
//     error carrying the validation class that refused it, never a blank stage.
//
// A fourth state, `offline`, is a refusal about the *connection* rather than about the machine, and
// is separate because the recovery is different: the others are answered by a decision and this one
// is answered by trying again.
//
// The dependencies are injected. This module imports no API client, no Supabase and no React: the
// arcade route is not behind the account boundary, so a static import of anything that can open a
// connection would fail `check:anonymous` — and a resolver that cannot reach the network is one
// that can be tested against every ordering without a server.

import { validateMachine, type ValidationIssue } from '@core/machine'
import { classOf } from '../builder/validationClasses'
import bundledMachineJson from '../../machines/neon-nova/machine.json?raw'
import type { OwnedMachine } from '@server/machines'
import { readStockMachine, stockMachine, type StockAccess } from './stockMachines'

/** Where a definition came from. Reported so a caller can say "cached" and a test can assert it. */
export type MachineOrigin = 'bundle' | 'cache' | 'network'

export interface ResolvedMachine {
  readonly kind: 'ok'
  readonly machineId: string
  readonly definition: unknown
  /**
   * The content hash, or nothing for the bundled flagship.
   *
   * Nothing rather than a sentinel. The bundle's hash is knowable only by hashing it, the bundle is
   * never cached and never fetched, and no caller has a use for it — so a made-up value here would
   * be a string that looks like a hash, compares unequal to the real one, and would eventually be
   * written into a cache key or a leaderboard argument by somebody who trusted it.
   */
  readonly definitionHash: string | undefined
  readonly source: MachineOrigin
}

export type RefusalReason = 'unknown' | 'needs-pro' | 'unreadable' | 'offline'

export interface RefusedMachine {
  readonly kind: 'refused'
  readonly reason: RefusalReason
  readonly machineId: string
  /**
   * What to tell the player, already specific.
   *
   * For `unreadable` this names the validation class that refused the document — 04 §16's own
   * numbering — because "this machine will not load" is a sentence that helps nobody and
   * "Rules (class 11): a rule refers to a mode nothing declares" is a sentence its author can act
   * on. Empty for the refusals whose message is the page itself.
   */
  readonly detail: string
}

export type MachineOutcome = ResolvedMachine | RefusedMachine

/** The flagship, parsed once. The one machine this product can always produce with no network. */
const BUNDLED = JSON.parse(bundledMachineJson) as { machineId: string }

/** The id of the machine in the bundle, read from the bytes rather than written beside them. */
export const BUNDLED_MACHINE_ID: string = BUNDLED.machineId

/**
 * The flagship, resolved without awaiting anything.
 *
 * Synchronous, and that is the point: `/` and `/play/neon-nova` are supposed to be instant, and a
 * promise that resolves in a microtask is one frame of empty stage on the two routes 08 §1 asks to
 * be immediate. `resolveMachine` calls this on its first branch, so there is one answer rather than
 * two that agree today.
 */
export function bundledMachine(): ResolvedMachine {
  return {
    kind: 'ok',
    machineId: BUNDLED_MACHINE_ID,
    definition: JSON.parse(bundledMachineJson) as unknown,
    definitionHash: undefined,
    source: 'bundle',
  }
}

/** One roster row, as much of it as choosing and fetching a machine needs. */
export interface RosterMachine {
  readonly slug: string
  readonly title: string
  readonly currentVersion: string
  readonly currentVersionId: string
  readonly definitionHash: string
  /**
   * `em-1970s`, `ss-1980s`, `dmd-1990s` or `lcd-modern`, when the roster said.
   *
   * Optional because nothing in resolving a machine needs it — it is what a tile shows in place of
   * a device count, which is a fact about the definition and therefore not in the roster. A row
   * that arrives without one is a row, not a fault.
   */
  readonly era?: string | undefined
  /**
   * What the author declared this machine to be (11 §6, M9-50).
   *
   * Optional for the same reason `era` is — nothing in *resolving* a machine needs it — but it is
   * carried because the arcade filters on it: the server has already dropped what the viewer
   * excluded, and `visibleRoster` is applied again to whatever this client is holding, because a
   * roster can be cached and this build might be talking to an older one. A row that arrives
   * without a rating is treated as the stricter of the two, which is `visibleAtRating`'s rule and
   * not this file's.
   */
  readonly contentRating?: 'everyone' | 'teen' | undefined
}

/**
 * The rows of `fetchMyMachines` that can actually be opened, in the shape the resolver reads.
 *
 * **This is the join between "yours" and "resolvable"**, and it is a function rather than a line
 * inside `useMachine` because it is the whole of what the fallback means: a machine with no saved
 * version has nothing to fetch, and offering it to `resolveMachine` would produce a `not_found`
 * from the server for a machine the author is looking straight at.
 */
export function openableRoster(owned: readonly OwnedMachine[]): readonly RosterMachine[] {
  return owned.flatMap((machine) =>
    machine.currentVersion === undefined ||
    machine.currentVersionId === undefined ||
    machine.definitionHash === undefined
      ? []
      : [
          {
            slug: machine.slug,
            title: machine.title,
            currentVersion: machine.currentVersion,
            currentVersionId: machine.currentVersionId,
            definitionHash: machine.definitionHash,
            ...(machine.era === '' ? {} : { era: machine.era }),
          },
        ],
  )
}

/**
 * What the resolver is allowed to reach.
 *
 * Every one of these is optional, and the shape of the module falls out of that: a caller with no
 * session supplies none of them and still gets the flagship, which is the anonymous path.
 */
export interface MachineSource {
  /** Existing account boundary verifies current Pro/guest access; an absent verifier unlocks nothing. */
  readonly stockAccess?: ((machineId: string) => Promise<StockAccess>) | undefined
  /** The roster, or nothing when there is no session, no network or no answer yet. */
  readonly roster?: readonly RosterMachine[] | undefined
  /** Read a definition previously fetched, by content hash. */
  readonly cached?: ((definitionHash: string) => Promise<unknown | undefined>) | undefined
  /** Store one. Failure is ignored by the caller; a cache that will not write is not an error. */
  readonly store?: ((definitionHash: string, definition: unknown, machineId: string, semver: string) => Promise<boolean>) | undefined
  /** Fetch a definition by version. `undefined` result means the request did not succeed. */
  readonly fetchVersion?:
    | ((machineId: string, semver: string) => Promise<{ definition: unknown; definitionHash: string } | 'refused' | 'offline' | undefined>)
    | undefined
}

/**
 * Resolve a machine id to a definition this build can run, or to a refusal that names itself.
 *
 * Deliberately takes the id a URL carries — the slug — rather than a uuid, because that is what a
 * person shares and what `/play/:machineId` matches. The roster maps it to the version to fetch.
 */
export async function resolveMachine(
  machineId: string,
  source: MachineSource = {},
): Promise<MachineOutcome> {
  // 1. The bundle. Before the roster is consulted, and before anything can be awaited: the front
  //    door must reach nothing even when a session and a roster happen to be in hand.
  if (machineId === BUNDLED_MACHINE_ID) return bundledMachine()

  const stock = stockMachine(machineId)
  if (stock !== undefined) {
    // The existing content-addressed cache policy permits previously downloaded games offline.
    // A shipped definition is not itself a grant: an uncached stock game still requires the
    // account boundary's actual entitlement/weekly-guest answer, never a localStorage tier hint.
    const held = await source.cached?.(stock.definitionHash)
    if (held !== undefined) {
      const problem = refusalFor(held)
      const identity = held as { machineId?: unknown; machineVersion?: unknown }
      if (problem !== undefined || identity.machineId !== machineId || identity.machineVersion !== stock.version)
        return { kind: 'refused', reason: 'unreadable', machineId, detail: problem ?? 'Cached machine identity does not match the chosen stock table' }
    } else {
      const access = await source.stockAccess?.(machineId) ?? 'needs-pro'
      if (access !== 'allowed') return { kind: 'refused', reason: access, machineId, detail: '' }
    }
    try {
      const definition = await readStockMachine(machineId)
      const problem = refusalFor(definition)
      if (problem !== undefined) return { kind: 'refused', reason: 'unreadable', machineId, detail: problem }
      void source.store?.(stock.definitionHash, definition, machineId, stock.version)
      return { kind: 'ok', machineId, definition, definitionHash: stock.definitionHash, source: 'bundle' }
    } catch (error) {
      return { kind: 'refused', reason: 'unreadable', machineId, detail: error instanceof Error ? error.message : 'Packaged stock data could not be verified' }
    }
  }

  const entry = source.roster?.find((row) => row.slug === machineId)
  if (entry === undefined) {
    // **Unknown, and only unknown when a roster was actually read.** With no roster in hand the
    // honest answer is that nothing is known, which is `offline` — a 404 for a machine that exists
    // and could not be listed would tell a player their link was wrong when it was not.
    return source.roster === undefined
      ? { kind: 'refused', reason: 'offline', machineId, detail: '' }
      : { kind: 'refused', reason: 'unknown', machineId, detail: '' }
  }

  // 2. The cache, keyed by the hash the roster just supplied. This is the whole reason the hash is
  //    in the cheap request rather than only in the expensive one.
  const cached = await source.cached?.(entry.definitionHash)
  if (cached !== undefined) {
    const problem = refusalFor(cached)
    if (problem !== undefined) return { kind: 'refused', reason: 'unreadable', machineId, detail: problem }
    return { kind: 'ok', machineId, definition: cached, definitionHash: entry.definitionHash, source: 'cache' }
  }

  // 3. The network.
  if (source.fetchVersion === undefined) {
    return { kind: 'refused', reason: 'offline', machineId, detail: '' }
  }
  const fetched = await source.fetchVersion(entry.slug, entry.currentVersion)
  if (fetched === 'refused') return { kind: 'refused', reason: 'needs-pro', machineId, detail: '' }
  if (fetched === 'offline' || fetched === undefined) {
    return { kind: 'refused', reason: 'offline', machineId, detail: '' }
  }

  const problem = refusalFor(fetched.definition)
  if (problem !== undefined) {
    return { kind: 'refused', reason: 'unreadable', machineId, detail: problem }
  }
  // Stored after validation, never before. A cache of documents this build refuses is a cache that
  // makes the second visit fail faster.
  void source.store?.(fetched.definitionHash, fetched.definition, entry.slug, entry.currentVersion)
  return {
    kind: 'ok',
    machineId,
    definition: fetched.definition,
    definitionHash: fetched.definitionHash,
    source: 'network',
  }
}

/**
 * Why this document is not a machine, in the words 04 §16 uses, or nothing.
 *
 * **The class, not the code.** `classOf` maps an issue code onto one of the sixteen validation
 * classes, which is what an author reads in the Builder's own panel — so a machine refused here and
 * the same machine refused at publish give the player the same word for the same problem.
 *
 * Warnings are not refusals. `validateMachine` warns about a guarded rule cycle, which every real
 * rulesheet contains and which 04's own worked example would fail on.
 */
export function refusalFor(definition: unknown): string | undefined {
  const report = validateMachine(definition)
  if (report.ok) return undefined
  const first = report.errors[0]
  if (first === undefined) return 'this machine did not pass validation'
  return describe(first)
}

function describe(issue: ValidationIssue): string {
  const cls = classOf(issue.code)
  const where = issue.path === '' ? '' : ` at ${issue.path}`
  return cls === undefined
    ? `${issue.message}${where}`
    : `${cls.title} (class ${cls.number}): ${issue.message}${where}`
}
